import { z } from '@faceup/validation'
import type { JSONContent } from '@tiptap/core'
import { LONG_STRING_MAX_LENGTH } from '../validationConfig'

export type { JSONContent as TiptapContent }

export const emptyTiptapContent = {
  type: 'doc',
  content: [{ type: 'paragraph' }],
} satisfies JSONContent

export const tiptapContentSchema = z
  .record(z.string(), z.unknown())
  .transform(v => v as JSONContent)

export const longTiptapContentSchema = tiptapContentSchema.refine(
  content => getTiptapContentTextLength(content) <= LONG_STRING_MAX_LENGTH,
  {
    message: `Maximum length is ${LONG_STRING_MAX_LENGTH} characters`,
  }
)

export const textToTiptapContent = (text: string | null | undefined): JSONContent | null => {
  if (!text) {
    return null
  }

  return {
    type: 'doc',
    content: text.split(/\r?\n/).map(paragraph => ({
      type: 'paragraph',
      ...(paragraph ? { content: [{ type: 'text', text: paragraph }] } : {}),
    })),
  }
}

export const getTiptapContentTextLength = (content: JSONContent | null): number => {
  // Backend validation can receive hand-crafted JSON, so avoid recursive traversal that could
  // overflow the call stack on a deeply nested payload.
  let nodes = content ? [content] : []
  let length = 0

  while (nodes.length > 0) {
    length += nodes.reduce(
      (sum, node) => sum + (typeof node.text === 'string' ? node.text.length : 0),
      0
    )
    nodes = nodes.flatMap(node => (Array.isArray(node.content) ? node.content : []))
  }

  return length
}

export const isTiptapContentEmpty = (content: JSONContent | null | undefined) =>
  getTiptapContentTextLength(content ?? null) === 0

const newlineAfterNodeTypes = new Set(['paragraph', 'heading', 'blockquote', 'codeBlock'])

type TiptapPlainTextStackItem = JSONContent | 'newline'

export const getTiptapContentPlainText = (content: JSONContent | null): string | null => {
  if (!content) {
    return null
  }

  let text = ''
  const stack: TiptapPlainTextStackItem[] = [content]

  while (stack.length > 0) {
    const item = stack.pop()

    if (!item) {
      continue
    }

    if (item === 'newline') {
      text += '\n'
      continue
    }

    if (typeof item.text === 'string') {
      text += item.text
      continue
    }

    const children = Array.isArray(item.content) ? item.content : []
    if (typeof item.type === 'string' && newlineAfterNodeTypes.has(item.type)) {
      stack.push('newline')
    }
    stack.push(...[...children].reverse())
  }

  const trimmedText = text.replace(/\n+$/, '').trim()
  return trimmedText.length > 0 ? trimmedText : null
}
